import { useState, useEffect } from 'react'
import { listSpaces, getSpaceContent, listContents } from '../lib/api'
import { contentTypeConfig } from '../lib/utils'
import {
  X,
  Search,
  Plus,
  FileText,
  Globe,
  Image as ImageIcon,
  Mic,
  BookOpen,
  File,
  Loader2,
  Check,
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Home,
} from 'lucide-react'

const iconMap = {
  note: FileText,
  bookmark: Globe,
  image: ImageIcon,
  pdf: FileText,
  docx: FileText,
  doc: FileText,
  xlsx: FileText,
  xls: FileText,
  pptx: FileText,
  ppt: FileText,
  txt: FileText,
  epub: BookOpen,
  transcript: Mic,
  unknown: File,
}

export function ContentChip({ content, onRemove, small = false }) {
  const Icon = iconMap[content.type] || File
  const config = contentTypeConfig[content.type] || contentTypeConfig.unknown

  return (
    <div className={`inline-flex items-center gap-1.5 ${small ? 'px-2 py-1' : 'px-2.5 py-1.5'} rounded-lg bg-purple-500/10 border border-purple-500/20`}>
      <Icon className={`${small ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} style={{ color: config.color }} />
      <span className={`${small ? 'text-[10px]' : 'text-xs'} font-medium text-purple-600 dark:text-purple-400 max-w-[120px] truncate`}>
        {content.title}
      </span>
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRemove(content)
          }}
          className="p-0.5 rounded hover:bg-purple-500/20 transition-colors"
        >
          <X className={`${small ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-purple-500`} />
        </button>
      )}
    </div>
  )
}

export function AttachedContents({ contents, onRemove, onAdd }) {
  if (contents.length === 0 && !onAdd) return null

  return (
    <div className="px-3 py-2 border-b border-border bg-muted/30">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <FileText className="w-3 h-3" />
          <span>Attached sources ({contents.length})</span>
        </div>
        {onAdd && (
          <button
            onClick={onAdd}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          >
            <Plus className="w-3 h-3" />
            Add
          </button>
        )}
      </div>
      {contents.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {contents.map((content) => (
            <ContentChip
              key={content.content_id || content.id}
              content={content}
              onRemove={onRemove}
              small
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function ContentPickerModal({ isOpen, onClose, onSelect, selectedIds = [] }) {
  const [spaces, setSpaces] = useState([])
  const [rootContents, setRootContents] = useState([])
  const [expandedSpaces, setExpandedSpaces] = useState({})
  const [spaceContents, setSpaceContents] = useState({})
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(new Set(selectedIds))
  const [allContents, setAllContents] = useState([]) // For tracking all loaded contents

  useEffect(() => {
    if (isOpen) {
      loadTree()
      setSelected(new Set(selectedIds))
    }
  }, [isOpen, selectedIds])

  const loadTree = async () => {
    setLoading(true)
    try {
      const spacesRes = await listSpaces()
      const spacesList = spacesRes.spaces || spacesRes || []
      setSpaces(spacesList)
      
      // Load root contents (contents without a space)
      const contentsRes = await listContents()
      const allContentsList = contentsRes.contents || contentsRes || []
      setAllContents(allContentsList)
      
      // Filter root contents (no space_id)
      const rootContentsList = allContentsList.filter(c => !c.space_id)
      setRootContents(rootContentsList)
    } catch (err) {
      console.error('Failed to load tree:', err)
    } finally {
      setLoading(false)
    }
  }

  const toggleSpace = async (spaceId) => {
    const isExpanded = expandedSpaces[spaceId]
    setExpandedSpaces(prev => ({ ...prev, [spaceId]: !isExpanded }))
    
    if (!isExpanded && !spaceContents[spaceId]) {
      try {
        const res = await getSpaceContent(spaceId)
        const contents = res.contents || res || []
        setSpaceContents(prev => ({ ...prev, [spaceId]: contents }))
        // Add to allContents for selection tracking
        setAllContents(prev => {
          const existingIds = new Set(prev.map(c => c.content_id || c.id))
          const newContents = contents.filter(c => !existingIds.has(c.content_id || c.id))
          return [...prev, ...newContents]
        })
      } catch (err) {
        console.error('Failed to load space contents:', err)
      }
    }
  }

  const toggleSelect = (content) => {
    const id = content.content_id || content.id
    const newSelected = new Set(selected)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelected(newSelected)
  }

  const handleConfirm = () => {
    const selectedContents = allContents.filter(c => selected.has(c.content_id || c.id))
    onSelect(selectedContents)
    onClose()
  }

  if (!isOpen) return null

  const renderContentItem = (content, indent = 0) => {
    const id = content.content_id || content.id
    const isSelected = selected.has(id)
    const Icon = iconMap[content.type] || File
    const config = contentTypeConfig[content.type] || contentTypeConfig.unknown

    return (
      <button
        key={id}
        onClick={() => toggleSelect(content)}
        className={`w-full flex items-center gap-2 py-2 px-2 rounded-lg text-left transition-all ${
          isSelected
            ? 'bg-primary/10'
            : 'hover:bg-accent'
        }`}
        style={{ paddingLeft: `${indent * 16 + 8}px` }}
      >
        <Icon className="w-4 h-4 flex-shrink-0" style={{ color: config.color }} />
        <span className="flex-1 text-sm truncate">{content.title}</span>
        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
          isSelected
            ? 'bg-primary border-primary'
            : 'border-muted-foreground/30'
        }`}>
          {isSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
        </div>
      </button>
    )
  }

  const renderSpace = (space, indent = 0) => {
    const isExpanded = expandedSpaces[space.id]
    const contents = spaceContents[space.id] || []
    const children = spaces.filter(s => s.parent_id === space.id)

    return (
      <div key={space.id}>
        <button
          onClick={() => toggleSpace(space.id)}
          className="w-full flex items-center gap-2 py-2 px-2 rounded-lg hover:bg-accent transition-colors"
          style={{ paddingLeft: `${indent * 16 + 8}px` }}
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          )}
          {isExpanded ? (
            <FolderOpen className="w-4 h-4 text-blue-500 flex-shrink-0" />
          ) : (
            <Folder className="w-4 h-4 text-blue-500 flex-shrink-0" />
          )}
          <span className="flex-1 text-sm font-medium truncate">{space.name}</span>
        </button>
        {isExpanded && (
          <div>
            {children.map(child => renderSpace(child, indent + 1))}
            {contents.map(content => renderContentItem(content, indent + 1))}
            {contents.length === 0 && children.length === 0 && (
              <div className="py-2 text-xs text-muted-foreground" style={{ paddingLeft: `${(indent + 1) * 16 + 8}px` }}>
                Empty folder
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  const rootSpaces = spaces.filter(s => !s.parent_id)

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[500px] md:max-h-[600px] bg-card rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Select Content</h2>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-accent">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tree View */}
        <div className="flex-1 overflow-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div>
              {/* Root Contents */}
              {rootContents.length > 0 && (
                <div className="mb-2">
                  <div className="flex items-center gap-2 py-2 px-2 text-xs text-muted-foreground font-medium">
                    <Home className="w-3.5 h-3.5" />
                    <span>Root</span>
                  </div>
                  {rootContents.map(content => renderContentItem(content, 1))}
                </div>
              )}
              
              {/* Spaces */}
              {rootSpaces.map(space => renderSpace(space))}
              
              {rootSpaces.length === 0 && rootContents.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  No content available
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {selected.size} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
